import { Button, type ButtonProps } from '@/components/Button'
import { cn } from '@/utilities/cn'
import NextLink from 'next/link'
import React from 'react'

import type { Page } from '@/payload-types'
import { ChevronRight } from 'lucide-react'
import { getLinkUrl } from '@/fields/link'
import { LINK_ICONS, type LinkIconName } from '@/fields/linkIcons'

/**
 * Link component with default JeugdFilms styling
 * Uses ocean-blue color with hover transition to primary pink
 */
export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string
  children: React.ReactNode
  className?: string
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href, children, className, ...props }, ref) => {
    return (
      <NextLink
        href={href}
        className={cn('text-ocean-blue hover:text-primary transition-colors inline', className)}
        ref={ref}
        {...props}
      >
        {children}
      </NextLink>
    )
  },
)
Link.displayName = 'Link'

export type CMSLinkType = {
  appearance?: 'inline' | 'link' | ButtonProps['variant']
  children?: React.ReactNode
  className?: string | null
  label?: string | null
  action?: boolean | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages'
    value: Page | string | number
  } | null
  size?: ButtonProps['size'] | null
  type?: 'custom' | 'reference' | null
  url?: string | null
  icon?: LinkIconName | null | 'none'
  disabled?: boolean | null
  textColor?: 'ocean-blue' | 'black' | 'primary' | null
}

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    label,
    newTab,
    reference,
    size: sizeFromProps,
    url,
    disabled,
    textColor = 'ocean-blue',
  } = props
  const icon = props.icon === 'none' ? undefined : props.icon

  const href = getLinkUrl(props)

  if (!href) return null

  const size = appearance === 'link' ? 'clear' : sizeFromProps
  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  /* Use styled Link component for inline appearance */
  if (appearance === 'inline') {
    // Map textColor to Tailwind classes
    const colorClasses: Record<string, string> = {
      'ocean-blue': 'text-ocean-blue hover:text-primary',
      'black': 'text-slate-900 hover:text-primary',
      'primary': 'text-primary hover:text-primary/80',
    }

    const textColorClass = colorClasses[textColor || 'ocean-blue'] || colorClasses['ocean-blue']

    return (
      <Link
        className={cn(textColorClass, className, disabled && 'opacity-50 pointer-events-none')}
        href={href || url || ''}
        {...newTabProps}
        aria-disabled={disabled || undefined}
      >
        {children ?? label}
      </Link>
    )
  }

  /* Render icon if specified */
  const IconComponent = icon ? LINK_ICONS[icon] : undefined

  /* Render as Button with specified variant (including 'link' variant) */
  return (
    <Button
      className={cn(className, 'group gap-2')}
      size={size}
      variant={appearance}
      disabled={disabled || undefined}
      asChild
    >
      <NextLink href={href || url || ''} {...newTabProps}>
        {props.action && (
          <div className={'transition-all duration-100 ease-in-out pr-2 group-hover:pr-0'}>
            <ChevronRight />
          </div>
        )}
        {IconComponent && <IconComponent size={16} />}
        {children ?? label}
      </NextLink>
    </Button>
  )
}
